import { sumBy } from 'es-toolkit'

import {
  applyResponsePartDelta,
  isModelRequestPart,
} from '@/components/chat-v2/lib/messageDelta'

import type {
  ChatMessage,
  ChatSubagentEvent,
  ModelRequestPart,
  ModelResponsePart,
  MsgDelta,
  SubagentChatSnapshot,
  SubagentEvent,
} from '@/types/chat-v2'

const EMPTY_SUBAGENT_SNAPSHOT: Readonly<SubagentChatSnapshot> = Object.freeze({
  version: 1,
  run_id: null,
  status: 'running',
  failure_message: null,
  messages: [],
})

export type SubagentSnapshotsByToolCallId = Readonly<
  Record<string, Readonly<SubagentChatSnapshot>>
>

export function resolveDisplayedSubagentSnapshot(
  persistedSubagentSnapshot: Readonly<SubagentChatSnapshot> | undefined,
  liveSubagentSnapshot: Readonly<SubagentChatSnapshot> | undefined,
): Readonly<SubagentChatSnapshot> {
  return (
    liveSubagentSnapshot ?? persistedSubagentSnapshot ?? EMPTY_SUBAGENT_SNAPSHOT
  )
}

export function applySubagentEventToSnapshot(
  snapshot: Readonly<SubagentChatSnapshot> | undefined,
  event: SubagentEvent,
): Readonly<SubagentChatSnapshot> {
  const baseSnapshot = snapshot ?? EMPTY_SUBAGENT_SNAPSHOT

  switch (event.event_type) {
    case 'channel:start': {
      const nextRunId = event.run_id ?? baseSnapshot.run_id
      if (
        snapshot !== undefined &&
        snapshot.status === 'running' &&
        snapshot.failure_message === null &&
        snapshot.run_id === nextRunId
      ) {
        return snapshot
      }
      return {
        ...baseSnapshot,
        run_id: nextRunId,
        status: 'running',
        failure_message: null,
      }
    }
    case 'channel:done': {
      const nextRunId = event.run_id ?? baseSnapshot.run_id
      if (
        snapshot !== undefined &&
        snapshot.status === 'completed' &&
        snapshot.failure_message === null &&
        snapshot.run_id === nextRunId
      ) {
        return snapshot
      }
      return {
        ...baseSnapshot,
        run_id: nextRunId,
        status: 'completed',
        failure_message: null,
      }
    }
    case 'channel:fail': {
      const nextRunId = event.run_id ?? baseSnapshot.run_id
      const nextFailureMessage = event.error_msg ?? baseSnapshot.failure_message
      if (
        snapshot !== undefined &&
        snapshot.status === 'failed' &&
        snapshot.run_id === nextRunId &&
        snapshot.failure_message === nextFailureMessage
      ) {
        return snapshot
      }
      return {
        ...baseSnapshot,
        run_id: nextRunId,
        status: 'failed',
        failure_message: nextFailureMessage,
      }
    }
    case 'channel:cancelled': {
      const nextRunId = event.run_id ?? baseSnapshot.run_id
      if (
        snapshot !== undefined &&
        snapshot.status === 'cancelled' &&
        snapshot.failure_message === null &&
        snapshot.run_id === nextRunId
      ) {
        return snapshot
      }
      return {
        ...baseSnapshot,
        run_id: nextRunId,
        status: 'cancelled',
        failure_message: null,
      }
    }
    case 'msg:final': {
      const nextRunId = event.run_id ?? baseSnapshot.run_id
      if (snapshot !== undefined && snapshot.run_id === nextRunId) {
        return snapshot
      }
      return {
        ...baseSnapshot,
        run_id: nextRunId,
      }
    }
    case 'msg:new': {
      const messagesById = new Map<string, ChatMessage>(
        baseSnapshot.messages.map((message) => [message.id, message]),
      )
      messagesById.set(event.message.id, event.message)
      return {
        ...baseSnapshot,
        run_id: event.message.run_id,
        messages: [...messagesById.values()].sort(
          (left, right) => left.index_in_thread - right.index_in_thread,
        ),
      }
    }
    case 'msg:delta': {
      if (
        (baseSnapshot.sequences?.[event.message_id] ?? -1) >= event.sequence
      ) {
        return baseSnapshot
      }
      const nextMessages = applyMsgDeltaToSubagentMessages(
        baseSnapshot.messages,
        event,
      )
      if (nextMessages === null) {
        return baseSnapshot
      }
      const nextRunId =
        event.delta.op === 'init_request' || event.delta.op === 'init_response'
          ? event.delta.run_id
          : baseSnapshot.run_id
      return {
        ...baseSnapshot,
        run_id: nextRunId,
        messages: nextMessages,
        sequences: {
          ...baseSnapshot.sequences,
          [event.message_id]: event.sequence,
        },
      }
    }
  }
}

function applyMsgDeltaToSubagentMessages(
  messages: readonly ChatMessage[],
  event: MsgDelta,
): ChatMessage[] | null {
  const messageId = event.message_id
  const delta = event.delta

  if (delta.op === 'init_request' || delta.op === 'init_response') {
    const messagesById = new Map<string, ChatMessage>(
      messages.map((message) => [message.id, message]),
    )
    const base = {
      id: messageId,
      parent_id: delta.parent_id,
      run_id: delta.run_id,
      run_parent_id: delta.run_parent_id,
      run_user_id: delta.run_user_id,
      index_in_thread: delta.index_in_thread,
    }
    messagesById.set(
      messageId,
      delta.op === 'init_request'
        ? { ...base, message: { kind: 'request', parts: [] } }
        : {
            ...base,
            message: {
              kind: 'response',
              parts: [],
              model_name: delta.model_name,
              timestamp: delta.timestamp,
            },
          },
    )
    return [...messagesById.values()].sort(
      (left, right) => left.index_in_thread - right.index_in_thread,
    )
  }

  const index = messages.findIndex((message) => message.id === messageId)
  if (index === -1) return null
  const existing = messages[index]

  const replaceAt = (updated: ChatMessage): ChatMessage[] => {
    const next = [...messages]
    next[index] = updated
    return next
  }

  switch (delta.op) {
    case 'set_part': {
      const updated = replaceMessagePartAt(
        existing,
        delta.part_index,
        delta.part,
      )
      if (!updated) return null
      return replaceAt(updated)
    }
    case 'apply_part_delta': {
      if (existing.message.kind !== 'response') return null
      const updatedPart = applyResponsePartDelta(
        existing.message.parts[delta.part_index],
        delta.delta,
      )
      if (!updatedPart) return null
      const nextParts = splicePartsAt(
        existing.message.parts,
        delta.part_index,
        updatedPart,
      )
      if (!nextParts) return null
      return replaceAt({
        ...existing,
        message: { ...existing.message, parts: nextParts },
      })
    }
    case 'set_deferred_tool_calls':
      return replaceAt({
        ...existing,
        deferred_tool_calls: delta.deferred_tool_calls,
      })
  }
}

function replaceMessagePartAt(
  message: ChatMessage,
  partIndex: number,
  part: ModelRequestPart | ModelResponsePart,
): ChatMessage | null {
  const existing = message.message
  if (existing.kind === 'request') {
    if (!isModelRequestPart(part)) return null
    const parts = splicePartsAt(existing.parts, partIndex, part)
    return parts ? { ...message, message: { ...existing, parts } } : null
  }
  if (isModelRequestPart(part)) return null
  const parts = splicePartsAt(existing.parts, partIndex, part)
  return parts ? { ...message, message: { ...existing, parts } } : null
}

function splicePartsAt<P>(
  parts: readonly P[],
  index: number,
  part: P,
): P[] | null {
  if (index < parts.length) {
    const next = [...parts]
    next[index] = part
    return next
  }
  return index === parts.length ? [...parts, part] : null
}

export function buildSubagentSnapshotFromEvents(
  events: ReadonlyArray<SubagentEvent> | undefined,
): Readonly<SubagentChatSnapshot> {
  if (!events || events.length === 0) {
    return EMPTY_SUBAGENT_SNAPSHOT
  }

  let snapshot: Readonly<SubagentChatSnapshot> | undefined
  for (const event of events) {
    snapshot = applySubagentEventToSnapshot(snapshot, event)
  }

  return snapshot ?? EMPTY_SUBAGENT_SNAPSHOT
}

const partContentSize = (
  part: ChatMessage['message']['parts'][number],
): number => {
  const contentSize =
    'content' in part && typeof part.content === 'string'
      ? part.content.length
      : 0
  const argsSize =
    'args' in part && part.args != null
      ? typeof part.args === 'string'
        ? part.args.length
        : JSON.stringify(part.args).length
      : 0
  return 1 + contentSize + argsSize
}

/**
 * Streamed content only accumulates (deltas append text/args; part-end
 * `set_part` replaces a draft with its completed form), so between two
 * copies of the same running stream the larger one is newer.
 */
const snapshotContentSize = (
  snapshot: Readonly<SubagentChatSnapshot>,
): number =>
  sumBy(snapshot.messages, (message) =>
    sumBy(message.message.parts, partContentSize),
  )

/**
 * Each message from whichever copy holds it at the later sequence: a
 * message the live copy has streamed past the fetched copy's sequence is
 * kept, so a resync never rewinds visible text.
 */
function mergeSubagentSnapshotsBySequence(
  live: Readonly<SubagentChatSnapshot>,
  fetched: Readonly<SubagentChatSnapshot>,
): Readonly<SubagentChatSnapshot> {
  const messagesById = new Map(
    live.messages.map((message) => [message.id, message]),
  )
  const sequences = { ...live.sequences }
  for (const message of fetched.messages) {
    const fetchedSequence = fetched.sequences?.[message.id]
    if (fetchedSequence === undefined) {
      messagesById.set(message.id, message)
    } else if (fetchedSequence >= (sequences[message.id] ?? -1)) {
      messagesById.set(message.id, message)
      sequences[message.id] = fetchedSequence
    }
  }
  return {
    ...fetched,
    messages: [...messagesById.values()].sort(
      (left, right) => left.index_in_thread - right.index_in_thread,
    ),
    sequences,
  }
}

/**
 * Picks between the client's live snapshot and a resync's fetched copy of
 * the same subagent stream.
 *
 * Terminal fetched copies are authoritative and always replace; a live
 * running stream absent from the fetch entirely (a snapshot written before
 * that subagent started) is kept. Two running copies merge per message by
 * sequence. A fetched copy without sequences (an older server) trails live
 * deltas by its flush interval, so the smaller copy is a stale flush:
 * taking it rewinds the visible Result text (llm-ui warns "Visible text
 * length exceeded target") and splices later deltas onto the rewound base
 * until the next part-end `set_part`.
 */
export function resolveResyncSubagentSnapshot(
  live: Readonly<SubagentChatSnapshot> | undefined,
  fetched: Readonly<SubagentChatSnapshot> | undefined,
): Readonly<SubagentChatSnapshot> | undefined {
  if (fetched === undefined) {
    return live?.status === 'running' ? live : undefined
  }
  if (live === undefined || live.status !== 'running') {
    return fetched
  }
  if (fetched.status !== 'running') {
    return fetched
  }
  if (fetched.sequences !== undefined) {
    return mergeSubagentSnapshotsBySequence(live, fetched)
  }
  return snapshotContentSize(fetched) < snapshotContentSize(live)
    ? live
    : fetched
}

function resolveSubagentSnapshotsByToolCallId(
  snapshotObjectIdsByToolCallId: Readonly<Record<string, string>> | undefined,
  snapshotObjectsById:
    | Readonly<Record<string, { snapshot: SubagentChatSnapshot }>>
    | undefined,
): Record<string, SubagentChatSnapshot> | undefined {
  if (!snapshotObjectIdsByToolCallId) {
    return undefined
  }

  const nextSnapshotsByToolCallId: Record<string, SubagentChatSnapshot> = {}
  for (const [toolCallId, objectId] of Object.entries(
    snapshotObjectIdsByToolCallId,
  )) {
    const snapshotObject = snapshotObjectsById?.[objectId]
    if (!snapshotObject) {
      continue
    }
    nextSnapshotsByToolCallId[toolCallId] = snapshotObject.snapshot
  }

  return Object.keys(nextSnapshotsByToolCallId).length > 0
    ? nextSnapshotsByToolCallId
    : undefined
}

export function mergePersistedAndLiveSubagentSnapshots(
  persistedSnapshotObjectIdsByToolCallId:
    | Readonly<Record<string, string>>
    | undefined,
  liveSnapshotObjectIdsByToolCallId:
    | Readonly<Record<string, string>>
    | undefined,
  snapshotObjectsById:
    | Readonly<Record<string, { snapshot: SubagentChatSnapshot }>>
    | undefined,
): Record<string, SubagentChatSnapshot> | undefined {
  const persisted = resolveSubagentSnapshotsByToolCallId(
    persistedSnapshotObjectIdsByToolCallId,
    snapshotObjectsById,
  )
  const live = resolveSubagentSnapshotsByToolCallId(
    liveSnapshotObjectIdsByToolCallId,
    snapshotObjectsById,
  )
  if (!persisted && !live) {
    return undefined
  }
  return {
    ...(persisted ?? {}),
    ...(live ?? {}),
  }
}

export function appendSubagentEventToByToolCallId(
  subagentSnapshotsByToolCallId: SubagentSnapshotsByToolCallId | undefined,
  event: ChatSubagentEvent,
): SubagentSnapshotsByToolCallId {
  const currentSnapshot =
    subagentSnapshotsByToolCallId?.[event.parent_tool_call_id]
  const nextSnapshot = applySubagentEventToSnapshot(
    currentSnapshot,
    event.subagent_event,
  )

  if (nextSnapshot === currentSnapshot && subagentSnapshotsByToolCallId) {
    return subagentSnapshotsByToolCallId
  }

  return {
    ...(subagentSnapshotsByToolCallId ?? {}),
    [event.parent_tool_call_id]: nextSnapshot,
  }
}
